import React, { useRef, useState, useEffect } from "react";
import { CheckCircle, ArchiveIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

interface PoemCardProps {
  poet: string;
  poemTitle: string;
  poemContent: string;
  onWin: () => void;
  isWinner: boolean;
  hasWinner: boolean;
  /**
   * Indicates if the poem has been archived in the database.
   * When true, the poem is displayed with a visual indicator showing it's archived.
   * Archived poems are still visible in historical deathmatches but won't appear in new selections.
   */
  isArchived?: boolean;
}

/**
 * PoemCard displays a single poem in the deathmatch contest with voting controls.
 * 
 * Features:
 * - Shows poem content in a scrollable area
 * - Provides a voting button to select the winning poem
 * - Highlights the winner with visual indicators
 * - Displays archive status for poems that have been archived
 * 
 * @param props - The component props
 * @returns A React component displaying a poem with interactive controls
 */
const PoemCard = ({ poet, poemTitle, poemContent, onWin, isWinner, hasWinner, isArchived = false }: PoemCardProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  useEffect(() => {
    // Check if this specific poem card's content is scrollable
    const checkScroll = () => {
      if (scrollAreaRef.current) {
        const viewport = scrollAreaRef.current.querySelector('.scroll-area-viewport') as HTMLElement;
        
        if (viewport) {
          const { scrollHeight, clientHeight } = viewport;
          const hasScrollableContent = scrollHeight > clientHeight;
          setCanScroll(hasScrollableContent);
          
          // Check initial scroll position
          const scrollPosition = viewport.scrollTop;
          const scrollBottom = scrollHeight - clientHeight;
          setIsAtBottom(Math.abs(scrollPosition - scrollBottom) < 10);
        }
      }
    };

    // Use a small delay to ensure DOM is fully rendered
    const timer = setTimeout(checkScroll, 100);
    
    window.addEventListener('resize', checkScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkScroll);
    };
  }, [poemContent]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    // Only update the scroll state for this specific card
    const viewport = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = viewport;
    setIsAtBottom(Math.abs(scrollTop + clientHeight - scrollHeight) < 10);
  };

  return (
    <div 
      className={cn(
        "book-shadow rounded-lg overflow-hidden transition-all duration-300 h-full flex flex-col",
        isWinner ? "ring-4 ring-amber-300 bg-amber-50" : "bg-white",
        hasWinner && !isWinner ? "opacity-70" : "",
        isArchived ? "bg-gray-100" : ""
      )}
      data-testid="poem-card"
      aria-label={`Poem: ${poemTitle} by ${poet}${isArchived ? ' (archived)' : ''}`}
    >
      <div className="p-4 flex flex-col h-full">
        {/* Archive indicator - displays a visual badge when a poem is archived */}
        {isArchived && (
          <div 
            className="mb-2 flex items-center justify-center gap-1 text-gray-500 text-sm bg-gray-100 py-1 px-2 rounded"
            title="This poem has been archived and won't appear in new deathmatches"
            data-testid="archive-indicator"
          >
            <ArchiveIcon className="h-3 w-3" />
            <span>Archived Poem</span>
          </div>
        )}
        
        <div className="mb-4 text-center">
          <Button
            onClick={onWin}
            disabled={hasWinner}
            variant={isWinner ? "default" : "outline"}
            className={cn(
              "w-full text-lg py-6 transition-all duration-300",
              isWinner ? "bg-amber-500 hover:bg-amber-600" : "hover:bg-amber-100 hover:text-amber-800",
              isArchived ? "opacity-90" : ""
            )}
            size="lg"
          >
            {isWinner ? (
              <span className="flex items-center justify-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Selected
              </span>
            ) : (
              "This poem moves on"
            )}
          </Button>
        </div>
        
        <h3 className="text-2xl font-poetry font-bold text-center mb-3 border-b pb-3">
          {poemTitle}
        </h3>
        
        <div className="text-sm text-gray-700 italic text-center mb-4">
          Inspired by the style of <span className="font-medium">{poet}</span>
        </div>
        
        <div ref={scrollAreaRef} className="relative flex-grow" style={{ minHeight: "200px" }}>
          <ScrollArea 
            className="h-full w-full custom-scrollbar" 
            style={{ maxHeight: "60vh" }}
            onScroll={handleScroll}
            type="always"
            scrollHideDelay={9999999}
          >
            <div className={cn(
              "font-mono text-sm whitespace-pre-wrap pb-4 pt-2 pr-2",
              isArchived ? "text-gray-600" : ""
            )}>
              {poemContent}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};

export default PoemCard;
